import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  RefObject,
  MutableRefObject,
} from "react";
import {
  calculateMinLimit,
  calculateMaxLimit,
  constrainToBounds,
  calculateContentFocalPoint,
  calculateZoomScaleLevels,
} from "../utils/zoomMath";
import getWindow from "../utils/window";
import useDevicePixelRatio from "./useDevicePixelRatio";
import { ClientPosition, SmoothZoomHandler } from "../types/zoom";

const PERCENTAGE_SCALE = 100;
const CONTENT_CENTER = 0.5;

interface ZoomPosition {
  x: number;
  y: number;
}

interface FocalPoint {
  focalX: number;
  focalY: number;
  containerWidth: number;
  containerHeight: number;
}

interface PendingClickZoom {
  type: "click";
  event: ClientPosition;
  index: number;
}

interface PendingSmoothZoom {
  type: "smooth";
  scaleFactor: number;
  focalClientX: number;
  focalClientY: number;
}

type PendingZoom = PendingClickZoom | PendingSmoothZoom;

interface UseZoomStateParams {
  containerRef: RefObject<HTMLElement>;
  contentWidth: number;
  contentHeight: number;
  zoomScaleLevels: number[];
  shouldResetZoom: boolean;
}

interface UseZoomStateReturn {
  currentScale: number;
  positionX: number;
  positionY: number;
  isZoomed: boolean;
  isMaxZoom: boolean;
  zoomScaleLevels: number[];
  currentScaleRef: MutableRefObject<number>;
  positionXRef: MutableRefObject<number>;
  positionYRef: MutableRefObject<number>;
  handleClickZoom: (event: ClientPosition) => void;
  handleSmoothZoom: SmoothZoomHandler;
  updatePosition: (x: number, y: number) => void;
  loadZoomedContent: boolean;
  zoomedContentReady: boolean;
  handleZoomedContentReady: () => void;
}

export default function useZoomState({
  containerRef,
  contentWidth,
  contentHeight,
  zoomScaleLevels: requestedZoomScaleLevels,
  shouldResetZoom,
}: UseZoomStateParams): UseZoomStateReturn {
  const [loadZoomedContent, setLoadZoomedContent] = useState<boolean>(false);
  const [zoomedContentReady, setZoomedContentReady] = useState<boolean>(false);
  const [currentScale, setCurrentScale] = useState<number>(0);
  const [positionX, setPositionX] = useState<number>(0);
  const [positionY, setPositionY] = useState<number>(0);
  const [clickZoomIndex, setClickZoomIndex] = useState<number>(0);

  const currentScaleRef = useRef<number>(0);
  const positionXRef = useRef<number>(0);
  const positionYRef = useRef<number>(0);
  const pendingZoomRef = useRef<PendingZoom | null>(null);

  const devicePixelRatio = useDevicePixelRatio();
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const measureContainer = () =>
      setContainerWidth(container.getBoundingClientRect().width);

    measureContainer();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(measureContainer);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [containerRef]);

  const zoomScaleLevels = useMemo(
    () =>
      calculateZoomScaleLevels({
        containerSize: containerWidth,
        contentSize: contentWidth,
        zoomScaleLevels: requestedZoomScaleLevels,
        devicePixelRatio,
      }),
    [containerWidth, contentWidth, requestedZoomScaleLevels, devicePixelRatio],
  );

  const maxScale = zoomScaleLevels[zoomScaleLevels.length - 1];
  const isZoomed = currentScale > 0;
  const isMaxZoom = isZoomed && currentScale === maxScale;

  useEffect(() => {
    if (shouldResetZoom) {
      setPositionX(0);
      setPositionY(0);
      positionXRef.current = 0;
      positionYRef.current = 0;
      setCurrentScale(0);
      currentScaleRef.current = 0;
      setClickZoomIndex(0);
    }
  }, [shouldResetZoom]);

  const calculatePosition = useCallback(
    (scale: number, focalX: number, focalY: number): ZoomPosition => {
      const { width: containerWidth, height: containerHeight } =
        containerRef.current.getBoundingClientRect();
      const scaledOffsetX = calculateMinLimit({
        elementSize: contentWidth,
        scale,
      });
      const maxOffsetX = calculateMaxLimit({
        containerSize: containerWidth,
        elementSize: contentWidth,
        scale,
      });
      const scaledOffsetY = calculateMinLimit({
        elementSize: contentHeight,
        scale,
      });
      const maxOffsetY = calculateMaxLimit({
        containerSize: containerHeight,
        elementSize: contentHeight,
        scale,
      });

      return {
        x: constrainToBounds(
          scaledOffsetX + focalX * (maxOffsetX - scaledOffsetX),
          scaledOffsetX,
          maxOffsetX,
        ),
        y: constrainToBounds(
          scaledOffsetY + focalY * (maxOffsetY - scaledOffsetY),
          scaledOffsetY,
          maxOffsetY,
        ),
      };
    },
    [containerRef, contentWidth, contentHeight],
  );

  const applyZoom = useCallback((scale: number, x: number, y: number): void => {
    currentScaleRef.current = scale;
    positionXRef.current = x;
    positionYRef.current = y;
    setCurrentScale(scale);
    setPositionX(x);
    setPositionY(y);
  }, []);

  const resetZoom = useCallback((): void => {
    currentScaleRef.current = 0;
    positionXRef.current = 0;
    positionYRef.current = 0;
    setCurrentScale(0);
    setPositionX(0);
    setPositionY(0);
    setClickZoomIndex(0);
  }, []);

  useEffect(() => {
    const win = getWindow();

    const constrainZoomToContainer = (): void => {
      const scale = currentScaleRef.current;

      if (scale === 0 || !containerRef.current) {
        return;
      }

      const { width: containerWidth, height: containerHeight } =
        containerRef.current.getBoundingClientRect();

      const newScale = constrainToBounds(
        scale,
        containerWidth / contentWidth,
        maxScale,
      );

      const x = constrainToBounds(
        positionXRef.current,
        calculateMinLimit({ elementSize: contentWidth, scale: newScale }),
        calculateMaxLimit({
          containerSize: containerWidth,
          elementSize: contentWidth,
          scale: newScale,
        }),
      );
      const y = constrainToBounds(
        positionYRef.current,
        calculateMinLimit({ elementSize: contentHeight, scale: newScale }),
        calculateMaxLimit({
          containerSize: containerHeight,
          elementSize: contentHeight,
          scale: newScale,
        }),
      );

      applyZoom(newScale, x, y);
    };

    win.addEventListener("resize", constrainZoomToContainer);

    return () => {
      win.removeEventListener("resize", constrainZoomToContainer);
    };
  }, [containerRef, contentWidth, contentHeight, maxScale, applyZoom]);

  const updatePosition = useCallback((x: number, y: number): void => {
    positionXRef.current = x;
    positionYRef.current = y;
    setPositionX(x);
    setPositionY(y);
  }, []);

  useEffect(() => {
    if (
      !zoomScaleLevels.length ||
      currentScaleRef.current <= maxScale ||
      !containerRef.current
    ) {
      return;
    }

    const { width: containerWidth, height: containerHeight } =
      containerRef.current.getBoundingClientRect();

    const x = constrainToBounds(
      positionXRef.current,
      calculateMinLimit({ elementSize: contentWidth, scale: maxScale }),
      calculateMaxLimit({
        containerSize: containerWidth,
        elementSize: contentWidth,
        scale: maxScale,
      }),
    );
    const y = constrainToBounds(
      positionYRef.current,
      calculateMinLimit({ elementSize: contentHeight, scale: maxScale }),
      calculateMaxLimit({
        containerSize: containerHeight,
        elementSize: contentHeight,
        scale: maxScale,
      }),
    );

    applyZoom(maxScale, x, y);
    setClickZoomIndex(zoomScaleLevels.length);
  }, [
    maxScale,
    containerRef,
    contentWidth,
    contentHeight,
    applyZoom,
    zoomScaleLevels,
  ]);

  const getFocalPointFromClient = useCallback(
    (clientX: number, clientY: number): FocalPoint => {
      const { top, left, width, height } =
        containerRef.current.getBoundingClientRect();
      return {
        focalX: (clientX - left) / width,
        focalY: (clientY - top) / height,
        containerWidth: width,
        containerHeight: height,
      };
    },
    [containerRef],
  );

  const handleClickZoom = useCallback(
    (event: ClientPosition): void => {
      if (!zoomScaleLevels.length) {
        return;
      }

      const nextIndex =
        clickZoomIndex >= zoomScaleLevels.length ? 0 : clickZoomIndex + 1;

      if (nextIndex === 0) {
        resetZoom();
        return;
      }

      if (!loadZoomedContent) {
        setLoadZoomedContent(true);
        pendingZoomRef.current = { type: "click", event, index: nextIndex };
        return;
      }

      if (!zoomedContentReady) {
        pendingZoomRef.current = { type: "click", event, index: nextIndex };
        return;
      }

      const {
        focalX: rawFocalX,
        focalY: rawFocalY,
        containerWidth,
        containerHeight,
      } = getFocalPointFromClient(event.clientX, event.clientY);

      let focalX = rawFocalX;
      let focalY = rawFocalY;

      if (currentScaleRef.current > 0) {
        const prevScale = currentScaleRef.current;
        const contentFocal = calculateContentFocalPoint({
          rawFocalX,
          rawFocalY,
          containerWidth,
          containerHeight,
          contentWidth,
          contentHeight,
          scale: prevScale,
          positionX: positionXRef.current,
          positionY: positionYRef.current,
        });
        focalX = contentFocal.focalX;
        focalY = contentFocal.focalY;

        const newScale = zoomScaleLevels[nextIndex - 1];
        const scaleRatio = prevScale / newScale;

        const newPosX =
          PERCENTAGE_SCALE * (focalX - CONTENT_CENTER) * (1 - scaleRatio) +
          scaleRatio * positionXRef.current;
        const newPosY =
          PERCENTAGE_SCALE * (focalY - CONTENT_CENTER) * (1 - scaleRatio) +
          scaleRatio * positionYRef.current;

        const newMinX = calculateMinLimit({
          elementSize: contentWidth,
          scale: newScale,
        });
        const newMaxX = calculateMaxLimit({
          containerSize: containerWidth,
          elementSize: contentWidth,
          scale: newScale,
        });
        const newMinY = calculateMinLimit({
          elementSize: contentHeight,
          scale: newScale,
        });
        const newMaxY = calculateMaxLimit({
          containerSize: containerHeight,
          elementSize: contentHeight,
          scale: newScale,
        });

        const x = constrainToBounds(newPosX, newMinX, newMaxX);
        const y = constrainToBounds(newPosY, newMinY, newMaxY);
        applyZoom(newScale, x, y);
        setClickZoomIndex(nextIndex);
        return;
      }

      const newScale = zoomScaleLevels[nextIndex - 1];
      const { x, y } = calculatePosition(newScale, focalX, focalY);
      applyZoom(newScale, x, y);
      setClickZoomIndex(nextIndex);
    },
    [
      clickZoomIndex,
      loadZoomedContent,
      zoomedContentReady,
      zoomScaleLevels,
      contentWidth,
      contentHeight,
      calculatePosition,
      applyZoom,
      resetZoom,
      getFocalPointFromClient,
    ],
  );

  const handleSmoothZoom = useCallback(
    (scaleFactor: number, focalClientX: number, focalClientY: number): void => {
      if (!zoomScaleLevels.length) {
        return;
      }

      if (!loadZoomedContent) {
        setLoadZoomedContent(true);
        pendingZoomRef.current = {
          type: "smooth",
          scaleFactor,
          focalClientX,
          focalClientY,
        };
        return;
      }

      if (!zoomedContentReady) {
        pendingZoomRef.current = {
          type: "smooth",
          scaleFactor,
          focalClientX,
          focalClientY,
        };
        return;
      }

      const {
        focalX: rawFocalX,
        focalY: rawFocalY,
        containerWidth,
        containerHeight,
      } = getFocalPointFromClient(focalClientX, focalClientY);

      const oldScale = currentScaleRef.current;
      const initialSmoothScale = containerWidth / contentWidth;

      if (oldScale === 0) {
        if (scaleFactor <= 1) {
          return;
        }
        const startScale = initialSmoothScale * scaleFactor;
        const { x, y } = calculatePosition(startScale, rawFocalX, rawFocalY);
        applyZoom(startScale, x, y);
        setClickZoomIndex(0);
        return;
      }

      const newScale = constrainToBounds(
        oldScale * scaleFactor,
        initialSmoothScale,
        maxScale,
      );

      if (newScale <= initialSmoothScale && scaleFactor < 1) {
        resetZoom();
        return;
      }

      const { focalX: contentFocalX, focalY: contentFocalY } =
        calculateContentFocalPoint({
          rawFocalX,
          rawFocalY,
          containerWidth,
          containerHeight,
          contentWidth,
          contentHeight,
          scale: oldScale,
          positionX: positionXRef.current,
          positionY: positionYRef.current,
        });

      const scaleRatio = oldScale / newScale;

      const newPosX =
        PERCENTAGE_SCALE * (contentFocalX - CONTENT_CENTER) * (1 - scaleRatio) +
        scaleRatio * positionXRef.current;
      const newPosY =
        PERCENTAGE_SCALE * (contentFocalY - CONTENT_CENTER) * (1 - scaleRatio) +
        scaleRatio * positionYRef.current;

      const newMinX = calculateMinLimit({
        elementSize: contentWidth,
        scale: newScale,
      });
      const newMaxX = calculateMaxLimit({
        containerSize: containerWidth,
        elementSize: contentWidth,
        scale: newScale,
      });
      const newMinY = calculateMinLimit({
        elementSize: contentHeight,
        scale: newScale,
      });
      const newMaxY = calculateMaxLimit({
        containerSize: containerHeight,
        elementSize: contentHeight,
        scale: newScale,
      });

      const x = constrainToBounds(newPosX, newMinX, newMaxX);
      const y = constrainToBounds(newPosY, newMinY, newMaxY);
      applyZoom(newScale, x, y);

      const nearestLevelIndex = zoomScaleLevels.reduce(
        (closest: number, level: number, index: number) =>
          Math.abs(level - newScale) <
          Math.abs(zoomScaleLevels[closest] - newScale)
            ? index
            : closest,
        0,
      );
      setClickZoomIndex(nearestLevelIndex + 1);
    },
    [
      loadZoomedContent,
      zoomedContentReady,
      zoomScaleLevels,
      contentWidth,
      contentHeight,
      maxScale,
      calculatePosition,
      applyZoom,
      resetZoom,
      getFocalPointFromClient,
    ],
  );

  const handleZoomedContentReady = (): void => {
    setZoomedContentReady(true);

    if (!pendingZoomRef.current) {
      return;
    }

    const pending = pendingZoomRef.current;
    pendingZoomRef.current = null;

    if (pending.type === "click") {
      const { focalX, focalY } = getFocalPointFromClient(
        pending.event.clientX,
        pending.event.clientY,
      );
      const newScale = zoomScaleLevels[pending.index - 1];
      const { x, y } = calculatePosition(newScale, focalX, focalY);
      applyZoom(newScale, x, y);
      setClickZoomIndex(pending.index);
    } else if (pending.type === "smooth") {
      const { focalX, focalY, containerWidth } = getFocalPointFromClient(
        pending.focalClientX,
        pending.focalClientY,
      );
      const initialSmoothScale = containerWidth / contentWidth;
      const startScale = initialSmoothScale * pending.scaleFactor;
      const { x, y } = calculatePosition(startScale, focalX, focalY);
      applyZoom(startScale, x, y);
    }
  };

  return {
    currentScale,
    positionX,
    positionY,
    isZoomed,
    isMaxZoom,
    zoomScaleLevels,
    currentScaleRef,
    positionXRef,
    positionYRef,
    handleClickZoom,
    handleSmoothZoom,
    updatePosition,
    loadZoomedContent,
    zoomedContentReady,
    handleZoomedContentReady,
  };
}
